
import { create } from 'zustand';
import { db } from '../services/db';
import { captureSilentError } from '../services/posthog';
import { supabase } from '../services/supabaseClient';
import { Category, FollowPreset, GoodShout, RecommendationStatus, Shout, ShoutFollow, User, Venue } from '../types';

/** Detect transient network errors that are worth retrying automatically */
export function isTransientError(error: any): boolean {
    const message = (error?.message || '').toLowerCase();
    return (
        message.includes('load failed') ||       // Safari fetch failure
        message.includes('failed to fetch') ||    // Chrome/Firefox fetch failure
        message.includes('networkerror') ||       // Firefox NetworkError
        message.includes('network request failed') ||
        error?.name === 'TypeError' && message.includes('cancelled')
    );
}

/** Retry an async operation once after a short delay for transient network errors */
async function withRetry<T>(fn: () => Promise<T>): Promise<T> {
    try {
        return await fn();
    } catch (error) {
        if (isTransientError(error)) {
            await new Promise(resolve => setTimeout(resolve, 1500));
            return await fn();
        }
        throw error;
    }
}

interface DataState {
    venues: Venue[];
    shouts: Shout[];
    users: User[];
    goodShouts: GoodShout[];
    isLoading: boolean;
    initialized: boolean;
    error: string | null;

    loadData: (user: User) => Promise<void>;
    refreshData: (user: User) => Promise<void>;
    
    addRecommendation: (user: User, data: any) => Promise<string | undefined>;
    updateShout: (user: User, shoutId: string, newStatus: RecommendationStatus, newDescription: string, category?: Category) => Promise<void>;
    deleteShout: (user: User, shoutId: string) => Promise<void>;
    addGoodShout: (user: User, shoutId: string, message: string) => Promise<void>;
    
    // Follows
    follows: ShoutFollow[];
    loadFollows: (user: User) => Promise<void>;
    followUser: (user: User, followedId: string, preset: FollowPreset) => Promise<void>;
    unfollowUser: (user: User, followedId: string) => Promise<void>;
    updateFollowPreset: (user: User, followedId: string, preset: FollowPreset) => Promise<void>;

    // Realtime
    subscribeToDataChanges: (user: User) => () => void;
    
    reset: () => void;
}

const debounce = <T extends (...args: any[]) => any>(fn: T, ms: number) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), ms);
    };
};

export const useDataStore = create<DataState>((set, get) => ({
    // ... existing store properties
    venues: [],
    shouts: [],
    users: [],
    goodShouts: [],
    follows: [], // Added follows
    isLoading: false,
    initialized: false,
    error: null,

    reset: () => {
        set({
            venues: [],
            shouts: [],
            users: [],
            goodShouts: [],
            follows: [], // Added follows to reset
            isLoading: false,
            initialized: false,
            error: null
        });
    },

    loadData: async (user: User) => {
        set({ isLoading: true, error: null });
        try {
            const { venues, shouts, users, goodShouts } = await withRetry(() => db.getFeed(user));
            const follows = await withRetry(() => db.getFollows(user.uid));

            set({ venues, shouts, users, goodShouts, follows, isLoading: false, initialized: true });
        } catch (e: any) {
            captureSilentError("useDataStore.loadData", e);
            const message = isTransientError(e)
                ? 'Connection issue — couldn\'t load your data.'
                : (e.message || 'Failed to load data');
            set({ error: message, isLoading: false, initialized: true });
        }
    },

    refreshData: async (user: User) => {
        // Silent refresh
        try {
            const data = await db.getFeed(user);
            set({ 
                venues: data.venues, 
                shouts: data.shouts, 
                users: data.users, 
                goodShouts: data.goodShouts 
            });
        } catch (e: any) {
            captureSilentError("useDataStore.refreshData", e);
        }
    },

    addRecommendation: async (user: User, data: any) => {
        const shoutId = crypto.randomUUID();
        // Set only if this call put a venue into the store, so a rollback undoes
        // our own work and nothing else.
        let addedVenueId: string | null = null;

        try {
            // Reuse an existing venue when we already know this Google Place, so two
            // shouts about the same place share one pin instead of minting a duplicate.
            // `venues` only holds this user's feed, so fall back to a DB lookup:
            // venues.google_place_id is unique, and a place first added by someone who
            // is not a friend would otherwise fail to insert with 23505.
            let existingVenue: Venue | undefined;
            if (data.googlePlaceId) {
                existingVenue = get().venues.find(v => v.googlePlaceId === data.googlePlaceId)
                    ?? (await withRetry(() => db.findVenueByGooglePlaceId(data.googlePlaceId)))
                    ?? undefined;
            }

            const venueId = existingVenue ? existingVenue.venueId : crypto.randomUUID();
            const newVenue: Venue | null = existingVenue ? null : {
                venueId,
                name: data.name,
                lat: data.lat,
                lng: data.lng,
                category: data.category,
                isCustom: data.isCustom,
                googlePlaceId: data.googlePlaceId
            };

            const newShout: Shout = {
                id: shoutId,
                venueId,
                userId: user.uid,
                status: data.status,
                description: data.description,
                createdAt: new Date().toISOString()
            };

            // A venue found via the DB lookup is not in the feed yet, so it still has
            // to go into the store for its pin to render.
            const venueForStore: Venue | null = newVenue
                ?? (get().venues.some(v => v.venueId === venueId) ? null : existingVenue!);

            // Optimistic update
            if (venueForStore) addedVenueId = venueId;
            set(state => ({
                venues: venueForStore ? [...state.venues, venueForStore] : state.venues,
                shouts: [...state.shouts, newShout]
            }));

            if (newVenue) {
                await withRetry(() => db.addVenue(newVenue));
            }
            await withRetry(() => db.addShout(newShout));
            return venueId;
        } catch (e: any) {
            captureSilentError("useDataStore.addRecommendation", e);
            // Rollback — only drop what this call added.
            set(state => ({
                venues: addedVenueId ? state.venues.filter(v => v.venueId !== addedVenueId) : state.venues,
                shouts: state.shouts.filter(s => s.id !== shoutId),
                error: e.message
            }));
            throw e;
        }
    },

    updateShout: async (user: User, shoutId: string, newStatus: RecommendationStatus, newDescription: string, category?: Category) => {
        const { shouts, venues } = get();
        const targetShout = shouts.find(s => s.id === shoutId);
        if (!targetShout) return;

        const updatedShout = { ...targetShout, status: newStatus, description: newDescription };

        // Optimistic update
        set(state => ({
            shouts: state.shouts.map(s => s.id === shoutId ? updatedShout : s),
            venues: category ? state.venues.map(v => v.venueId === targetShout.venueId ? { ...v, category } : v) : state.venues
        }));

        try {
            await withRetry(() => db.updateShout(updatedShout, category));
        } catch (e: any) {
            captureSilentError("useDataStore.updateShout", e);
            // Rollback (requires keeping previous state or re-fetching)
            // For now, just re-fetch to be safe/lazy
            get().refreshData(user);
            throw e;
        }
    },

    deleteShout: async (user: User, shoutId: string) => {
        // Optimistic
        set(state => ({
            shouts: state.shouts.filter(s => s.id !== shoutId)
        }));

        try {
            await withRetry(() => db.deleteShout(shoutId));
        } catch (e: any) {
            captureSilentError("useDataStore.deleteShout", e);
            get().refreshData(user);
            throw e;
        }
    },

    addGoodShout: async (user: User, shoutId: string, message: string) => {
        const newGoodShout: GoodShout = {
            id: crypto.randomUUID(),
            shoutId,
            userId: user.uid,
            message,
            createdAt: new Date().toISOString()
        };

        set(state => ({
            goodShouts: [newGoodShout, ...state.goodShouts]
        }));

        try {
            await withRetry(() => db.addGoodShout(newGoodShout));
        } catch (e: any) {
            captureSilentError("useDataStore.addGoodShout", e);
             set(state => ({
                goodShouts: state.goodShouts.filter(gs => gs.id !== newGoodShout.id)
            }));
            throw e;
        }
    },



    loadFollows: async (user: User) => {
        try {
            const follows = await db.getFollows(user.uid);
            set({ follows });
        } catch (e: any) {
            captureSilentError("useDataStore.loadFollows", e);
        }
    },

    followUser: async (user: User, followedId: string, preset: FollowPreset) => {
        // Optimistic update
        const newFollow: ShoutFollow = {
            followerId: user.uid,
            followedId,
            notifyPreset: preset,
            createdAt: new Date().toISOString()
        };

        const prevFollows = get().follows;
        set({ follows: [...prevFollows, newFollow] });

        try {
            await db.followUser(user.uid, followedId, preset);
        } catch (e: any) {
            captureSilentError("useDataStore.followUser", e);
            set({ follows: prevFollows, error: 'Failed to follow user' });
        }
    },

    unfollowUser: async (user: User, followedId: string) => {
        // Optimistic update
        const prevFollows = get().follows;
        set({ follows: prevFollows.filter(f => f.followedId !== followedId) });

        try {
            await db.unfollowUser(user.uid, followedId);
        } catch (e: any) {
            captureSilentError("useDataStore.unfollowUser", e);
            set({ follows: prevFollows, error: 'Failed to unfollow user' });
        }
    },

    updateFollowPreset: async (user: User, followedId: string, preset: FollowPreset) => {
        // Optimistic update
        const prevFollows = get().follows;
        set({ 
            follows: prevFollows.map(f => 
                f.followedId === followedId ? { ...f, notifyPreset: preset } : f
            ) 
        });

        try {
            await db.updateFollowPreset(user.uid, followedId, preset);
        } catch (e: any) {
            captureSilentError("useDataStore.updateFollowPreset", e);
            set({ follows: prevFollows, error: 'Failed to update follow settings' });
        }
    },

    subscribeToDataChanges: (user: User) => {
        const debouncedRefresh = debounce(() => get().refreshData(user), 300);

        // Listen to changes in Shouts and Venues (and Friendships if possible)
        // We only care about ensuring our feed is up to date.
        // The simplest strategy is: if ANYTHING changes that might affect us, refresh the data.
        
        // Channels
        const channel = supabase.channel('global-data-changes')
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'shouts' },
                () => {
                   // Optimization: Check if the shout is relevant (e.g. from a friend)
                   // But for now, just refresh. It's safer.
                   debouncedRefresh(); 
                }
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'venues' },
                 () => {
                   debouncedRefresh(); 
                }
            )
             .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'good_shouts' },
                 () => {
                   debouncedRefresh(); 
                }
            )
             // Follows changes (sync across devices)
             .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'shout_follows' },
                (payload) => {
                    const newData = payload.new as { follower_id?: string } | null;
                    const oldData = payload.old as { follower_id?: string } | null;
                    
                    const affectsCurrentUser = 
                        (newData && newData.follower_id === user.uid) ||
                        (oldData && oldData.follower_id === user.uid);
                    
                    // NOTE: This client-side check is technically redundant because RLS on 'shout_follows'
                    // already filters events for us (policy: auth.uid() = follower_id).
                    // However, we are keeping it for now until we've fully verified the realtime RLS behavior in production.
                    if (affectsCurrentUser) {
                        get().loadFollows(user);
                    }
                }
            )
             // Friendships changes (e.g. removed friend)
             .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'friendships' },
                (payload) => {
                    const newData = payload.new as { user_id_1?: string; user_id_2?: string } | null;
                    const oldData = payload.old as { user_id_1?: string; user_id_2?: string } | null;
                    
                    const affectsCurrentUser = 
                        (newData && (newData.user_id_1 === user.uid || newData.user_id_2 === user.uid)) ||
                        (oldData && (oldData.user_id_1 === user.uid || oldData.user_id_2 === user.uid));
                    
                    if (affectsCurrentUser) {
                        debouncedRefresh();
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }
}));
